import { getGitHubEndpoints } from './config.js';

/**
 * First path segments that github.com reserves for site routes, so
 * `github.com/<segment>/...` is never an `owner/repo`. Without this guard the
 * deep-link tolerance below would read `github.com/orgs/acme/projects/3` as the
 * repo `orgs/acme`.
 */
const RESERVED_OWNER_SEGMENTS = new Set([
  'about',
  'account',
  'apps',
  'codespaces',
  'collections',
  'contact',
  'dashboard',
  'enterprises',
  'events',
  'explore',
  'features',
  'issues',
  'login',
  'logout',
  'marketplace',
  'new',
  'notifications',
  'organizations',
  'orgs',
  'pricing',
  'pulls',
  'search',
  'security',
  'sessions',
  'settings',
  'signup',
  'sponsors',
  'stars',
  'topics',
  'trending',
  'users',
]);

/**
 * Exact browser-editor hosts that open a repo in place. `github.dev` mirrors
 * github.com's paths, while vscode.dev namespaces the provider
 * (`vscode.dev/github/owner/repo`).
 */
const GITHUB_DEV_HOST = 'github.dev';
const VSCODE_DEV_HOSTS = new Set(['vscode.dev', 'insiders.vscode.dev']);

/** Strip the clone-only `.git` suffix from a repository segment. */
function stripGitSuffix(segment: string): string {
  return segment.toLowerCase().endsWith('.git') ? segment.slice(0, -4) : segment;
}

function repoFromPath(path: string, segmentOffset = 0): { owner: string; repo: string } | null {
  const pathWithoutUrlTail = path.split(/[?#]/, 1)[0];
  const parts = pathWithoutUrlTail.split('/').filter((part) => part.length > 0);
  if (parts.length < segmentOffset + 2) return null;
  const owner = parts[segmentOffset];
  const repo = stripGitSuffix(parts[segmentOffset + 1]);
  if (!owner || !repo || RESERVED_OWNER_SEGMENTS.has(owner.toLowerCase())) return null;
  return { owner, repo };
}

/**
 * Parse a GitHub URL or owner/repo shorthand into { owner, repo }. Returns null
 * for non-GitHub URLs.
 *
 * Deep links resolve to the repository they belong to: everything past
 * `owner/repo` (`/pull/123`, `/pull/123/files`, `/tree/main`, `/blob/...`),
 * along with a `?query` or `#fragment`, is an address *within* a repo rather
 * than a different repo. Pasting the URL out of a browser address bar is how
 * people name a repo, and a link copied off a PR review comment carries all of
 * the above at once. The same holds for links out of browser editors.
 */
export function parseGitHubRepo(
  url: string | null | undefined,
): { owner: string; repo: string } | null {
  if (!url || !url.trim()) return null;
  const s = url.trim();

  if (/^[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+$/.test(s)) {
    const [owner, rest] = s.split('/');
    // `owner/repo.git` is how a clone command names a repo; strip the suffix
    // here too so the shorthand and URL forms agree on the slug.
    const repo = stripGitSuffix(rest);
    if (!repo) return null;
    return { owner, repo };
  }

  const endpoints = getGitHubEndpoints();
  const githubHosts = new Set(['github.com', 'www.github.com', endpoints.host.toLowerCase()]);

  // SCP-style git remotes are not URLs, so parse them separately and require
  // the host to match exactly. Searching for a host substring would accept a
  // lookalike such as `notgithub.com`.
  const scp = /^[^\s/@]+@([^\s/:@]+):(.+)$/.exec(s);
  if (scp) {
    if (!githubHosts.has(scp[1].toLowerCase())) return null;
    return repoFromPath(scp[2]);
  }

  // Accept bare `github.com/owner/repo` in addition to normal absolute URLs,
  // but only when the leading token is itself an exact supported host.
  const leadingHost = s.split('/', 1)[0].toLowerCase();
  const browserEditorHosts = new Set([GITHUB_DEV_HOST, ...VSCODE_DEV_HOSTS]);
  const webInput =
    githubHosts.has(leadingHost) || browserEditorHosts.has(leadingHost) ? `https://${s}` : s;

  let web: URL;
  try {
    web = new URL(webInput);
  } catch {
    return null;
  }
  const isWebProtocol = web.protocol === 'http:' || web.protocol === 'https:';
  const isGitProtocol = web.protocol === 'ssh:' || web.protocol === 'git:';
  const hostname = web.hostname.toLowerCase();
  if (
    (isWebProtocol || isGitProtocol) &&
    (githubHosts.has(web.host.toLowerCase()) || githubHosts.has(hostname))
  ) {
    return repoFromPath(web.pathname);
  }
  if (!isWebProtocol) return null;
  if (hostname === GITHUB_DEV_HOST) return repoFromPath(web.pathname);
  if (!VSCODE_DEV_HOSTS.has(hostname)) return null;

  const parts = web.pathname.split('/').filter((part) => part.length > 0);
  if (parts[0]?.toLowerCase() !== 'github') return null;
  return repoFromPath(web.pathname, 1);
}

/** Extract owner/repo slug from a GitHub URL or shorthand. Returns `"owner/repo"` or null. */
export function toRepoSlug(url: string | null | undefined): string | null {
  const parsed = parseGitHubRepo(url);
  return parsed ? `${parsed.owner}/${parsed.repo}` : null;
}

/** @deprecated Use toRepoSlug instead */
export const toRepoFullName = toRepoSlug;

/** Returns the HTTPS clone URL for a GitHub repository, on the configured
 * GitHub host (github.com by default, or the GitHub Enterprise Server host). */
export function toGitHubRepoUrl(repoSlug: string): string {
  return `${getGitHubEndpoints().webBaseUrl}/${repoSlug}.git`;
}

/** Normalize a repo URL to full HTTPS clone URL. Passes through non-GitHub URLs unchanged. */
export function normalizeRepoUrl(url: string | null | undefined): string | null {
  if (!url || !url.trim()) return null;
  const parsed = parseGitHubRepo(url);
  if (parsed) return toGitHubRepoUrl(`${parsed.owner}/${parsed.repo}`);
  return url.trim();
}

/**
 * The directory name a repo clones into: the slug's last path component with a
 * trailing `.git` stripped. This mirrors `git clone`'s own default-directory
 * rule, so it is the authority for the on-box clone path — unlike a
 * `parseGitHubRepo`-based derivation, it stays correct for non-GitHub URLs
 * (which `parseGitHubRepo` returns null for). Falls back to `repo` for a slug
 * with no usable trailing segment (e.g. a trailing-slash URL).
 */
export function repoNameFromSlug(slug: string): string {
  return (
    slug
      .split('/')
      .pop()!
      .replace(/\.git$/, '') || 'repo'
  );
}

/**
 * The on-box path a repo clones to. Single source of truth shared by the clone
 * command builder and prompt builders that reference the clone location before
 * the clone runs, so the prompt can never disagree with reality.
 */
export function gitRepoClonePath(slug: string): string {
  return `/home/user/${repoNameFromSlug(slug)}`;
}
